import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import {
  GrowthBook,
  useGrowthBook as useGBHook,
} from "@growthbook/growthbook-react";
import isEqual from "lodash/isEqual";

import { useGetManagerProfile } from "manager/hooks/api";

import {
  getCachedActiveCompanyId,
  getCachedActiveCompanySlug,
  getClaims,
  Token,
  useClaims,
} from "./auth";

type GBContext = {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyId?: number;
  companySlug?: string;
  env?: string;
  role?: string;
  propertyLocation?: string;
};

const getGBAttributes = (
  params: {
    token?: Token;
    companyId?: number;
    companySlug?: string;
    role?: string;
    propertyLocation?: string;
  } = {}
): GBContext => {
  const token = params.token ?? getClaims();

  return {
    id: (token?.userId || token?.sub || "anonymous").toString(),
    email: token?.email || token?.username,
    firstName: token?.givenName,
    lastName: token?.familyName,
    companyId: params.companyId ?? getCachedActiveCompanyId(),
    companySlug: params.companySlug ?? getCachedActiveCompanySlug(),
    env: process.env.REACT_APP_ENVIRONMENT,
    role: params.role,
    propertyLocation: params.propertyLocation,
  };
};

// GrowthBook configuration with streaming enabled
const getGrowthBookConfig = () => {
  const apiHost =
    process.env.REACT_APP_GROWTHBOOK_API_HOST || "https://cdn.growthbook.io";
  const clientKey = process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY;
  if (!clientKey) {
    console.warn("GrowthBook client key not configured");
    return null;
  }

  return {
    apiHost,
    clientKey,
    // Enable streaming for real-time updates
    streaming: true,
    enableDevMode: process.env.NODE_ENV === "development",
    subscribeToChanges: true,
    trackingCallback: (experiment: any, result: any) => {
      // Track in Datadog RUM
      datadogRum.addAction("growthbook_experiment_viewed", {
        experiment_key: experiment.key,
        variation_id: result.variationId,
        value: result.value,
        hash_used: result.hashUsed,
        in_experiment: result.inExperiment,
      });

      // Also add as feature flag evaluation
      datadogRum.addFeatureFlagEvaluation(experiment.key, result.value);
    },
  };
};

// Helper function to retry GrowthBook init with exponential backoff
const initGrowthBookWithRetry = async (
  gbInstance: GrowthBook,
  config: any,
  maxRetries: number = 3
): Promise<any> => {
  let lastError: any = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      datadogRum.addAction("growthbook_init_attempt", {
        attempt,
        maxRetries,
        timeout: config.timeout,
      });

      // eslint-disable-next-line no-await-in-loop
      const initResult = await gbInstance.init(config);

      if (initResult.success) {
        datadogRum.addAction("growthbook_init_success", {
          attempt,
          totalAttempts: attempt,
          source: initResult.source,
          success: true,
        });
        return initResult;
      }

      // Init returned but wasn't successful
      lastError = initResult.error;
      datadogRum.addAction("growthbook_init_attempt_failed", {
        attempt,
        error: initResult.error?.message || "Unknown error",
        source: initResult.source || "unknown",
      });
    } catch (error: any) {
      lastError = error;
      datadogRum.addAction("growthbook_init_attempt_error", {
        attempt,
        error: error?.message || error?.toString() || "Unknown error",
        errorType: error?.name || "Error",
      });
    }

    // Wait before retry with exponential backoff
    if (attempt < maxRetries) {
      const delay = 2 ** (attempt - 1) * 1000; // 1s, 2s, 4s
      datadogRum.addAction("growthbook_init_retry_wait", {
        attempt,
        nextAttempt: attempt + 1,
        delayMs: delay,
      });
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // All retries failed
  datadogRum.addError(
    new Error(`GrowthBook init failed after ${maxRetries} attempts`),
    {
      source: "growthbook",
      maxRetries,
      lastError: lastError?.message || lastError?.toString() || "Unknown error",
    }
  );

  return {
    success: false,
    error: lastError,
    retriesExhausted: true,
  };
};

// Create singleton GrowthBook instance
let gbInstance: GrowthBook | null = null;
let gbClientKeySuffix: string | null = null;
let gbInitSucceeded = false;

// Export function to check if GrowthBook actually initialized successfully
export const isGrowthBookInitialized = () => gbInitSucceeded;

export const getGrowthBookInstance = (): GrowthBook => {
  if (!gbInstance) {
    const config = getGrowthBookConfig();
    if (!config) {
      // Return a minimal instance if not configured
      // This allows the app to function without GrowthBook configured
      datadogRum.addAction("growthbook_not_configured", {
        message: "GrowthBook client key not configured, using minimal instance",
      });
      gbInstance = new GrowthBook({
        attributes: getGBAttributes(),
        features: {},
      });
    } else {
      try {
        const initialAttributes = getGBAttributes();

        datadogRum.addAction("growthbook_instance_creation", {
          apiHost: config.apiHost,
          hasClientKey: !!config.clientKey,
          clientKeySuffix: (config.clientKey || "").slice(-6) || null,
          initialAttributes,
        });

        // Cache suffix for future logs
        gbClientKeySuffix = (config.clientKey || "").slice(-6) || null;

        gbInstance = new GrowthBook({
          apiHost: config.apiHost,
          clientKey: config.clientKey,
          enableDevMode: config.enableDevMode,
          subscribeToChanges: config.subscribeToChanges,
          trackingCallback: config.trackingCallback,
          attributes: initialAttributes,
        });

        // Load features from the API with streaming enabled and retry on failure
        const initConfig = {
          streaming: true,
          skipCache: true,
          timeout: 5000, // 5 second timeout per attempt
        };

        // Use async IIFE to handle async init with retries
        (async () => {
          const initResult = await initGrowthBookWithRetry(
            gbInstance!,
            initConfig
          );

          if (!initResult.success) {
            const errorMessage = initResult.error?.message || "Unknown error";
            gbInitSucceeded = false;
            datadogRum.addError(
              new Error(
                `GrowthBook init failed after retries: ${errorMessage}`
              ),
              {
                source: "growthbook",
                initResult,
                retriesExhausted: initResult.retriesExhausted,
              }
            );
            // Don't throw - continue with existing instance
          } else {
            gbInitSucceeded = true;
            datadogRum.addAction("growthbook_initialized", {
              source: initResult.source,
              success: initResult.success,
              clientKeySuffix: gbClientKeySuffix,
            });
          }
        })().catch((error) => {
          datadogRum.addError(new Error("GrowthBook init promise failed"), {
            source: "growthbook",
            error: error.message,
            clientKeySuffix: gbClientKeySuffix,
          });
        });
      } catch (error) {
        datadogRum.addError(new Error("Failed to initialize GrowthBook"), {
          source: "growthbook",
          error,
          clientKeySuffix: gbClientKeySuffix,
        });
        // Return minimal instance on error
        gbInstance = new GrowthBook({
          attributes: getGBAttributes(),
          features: {},
          enabled: false,
        });
      }
    }
  }

  return gbInstance;
};

// Component to watch and update GrowthBook attributes when user context changes
export const WatchGrowthBook = () => {
  const gb = useGBHook();
  const { claims, company } = useClaims();

  useEffect(() => {
    if (!gb || !gb.setAttributes) return;

    // Get current attributes to preserve propertyLocation
    const currentAttributes = gb.getAttributes ? gb.getAttributes() : {};

    // Update attributes when user context changes
    const attributes = getGBAttributes({
      token: claims,
      companyId: company?.id,
      companySlug: company?.slug,
      propertyLocation: currentAttributes.propertyLocation,
    });

    gb.setAttributes(attributes);
  }, [gb, claims, company]);

  return null;
};

// Custom hook for accessing GrowthBook with Vero context
export const useVeroGrowthBook = () => {
  const gb = useGBHook();
  const { data: profile } = useGetManagerProfile();
  const { claims, company } = useClaims();

  useEffect(() => {
    if (!gb) return;

    // Get current and new attributes
    const currentAttributes = gb.getAttributes ? gb.getAttributes() : {};
    const newAttributes = getGBAttributes({
      token: claims,
      companyId: company?.id,
      companySlug: company?.slug,
      role: profile?.role,
      propertyLocation: currentAttributes.propertyLocation,
    });

    // Check if attributes actually changed
    const attributesChanged = !isEqual(currentAttributes, newAttributes);

    if (!attributesChanged) {
      // No need to update or refresh if attributes haven't changed
      return;
    }

    if (gb.setAttributes) {
      gb.setAttributes(newAttributes);
    }

    // Force refresh features when user or company context changes
    // Note: Even with streaming, refresh is needed when attributes change
    // because streaming only updates feature definitions, not re-evaluates with new attributes
    if (
      (claims?.userId || claims?.sub || company?.id || company?.slug) &&
      gb.refreshFeatures
    ) {
      const refreshStartTime = Date.now();

      // refreshFeatures returns Promise<void>, resolves on success, rejects on error
      gb.refreshFeatures({
        skipCache: true,
        timeout: 3000, // 3 second timeout for refresh
      })
        .then(() => {
          const refreshDuration = Date.now() - refreshStartTime;

          // Success - features were refreshed
          datadogRum.addAction("growthbook_refresh_success", {
            attributes: newAttributes,
            previousAttributes: currentAttributes,
            refreshDuration,
            featuresCount: gb.getFeatures
              ? Object.keys(gb.getFeatures() || {}).length
              : 0,
            gbReady: gb.ready,
            reason: "refresh_after_attribute_change",
            clientKeySuffix: gbClientKeySuffix,
            trigger: {
              hasUserId: !!claims?.userId || !!claims?.sub,
              hasCompany: !!company?.id || !!company?.slug,
              userId: claims?.userId || claims?.sub,
              companyId: company?.id,
              companySlug: company?.slug,
            },
          });
        })
        .catch((error) => {
          const refreshDuration = Date.now() - refreshStartTime;

          // Failed to refresh features
          datadogRum.addAction("growthbook_refresh_failed", {
            error: error?.message || error?.toString() || "Unknown error",
            errorType: error?.name || "Error",
            errorStack: error?.stack,
            attributes: newAttributes,
            previousAttributes: currentAttributes,
            refreshDuration,
            gbReady: gb.ready,
            gbFeatures: gb.getFeatures
              ? Object.keys(gb.getFeatures() || {})
              : [],
            featuresCount: gb.getFeatures
              ? Object.keys(gb.getFeatures() || {}).length
              : 0,
            reason: "refresh_after_attribute_change",
            clientKeySuffix: gbClientKeySuffix,
            trigger: {
              hasUserId: !!claims?.userId || !!claims?.sub,
              hasCompany: !!company?.id || !!company?.slug,
              userId: claims?.userId || claims?.sub,
              companyId: company?.id,
              companySlug: company?.slug,
            },
          });

          // Also log as error for better tracking
          datadogRum.addError(
            new Error(
              `GrowthBook refresh failed: ${error?.message || "Unknown error"}`
            ),
            {
              source: "growthbook",
              error: error?.message,
              errorStack: error?.stack,
              attributes: newAttributes,
              refreshDuration,
              clientKeySuffix: gbClientKeySuffix,
            }
          );
        });
    }
  }, [gb, claims?.userId, claims?.sub, company?.id, company?.slug]);

  return gb;
};

// Helper functions to match LaunchDarkly interface
export const useGrowthBookFeature = (
  key: string,
  defaultValue: any = false
) => {
  const gb = useVeroGrowthBook();

  if (!gb || !gb.getFeatureValue) return defaultValue;

  return gb.getFeatureValue(key, defaultValue);
};

export const useGrowthBookFeatures = () => {
  const gb = useVeroGrowthBook();

  if (!gb || !gb.getFeatures || !gb.getFeatureValue) return {};

  // Get all feature values as an object
  const features: Record<string, any> = {};
  const allFeatures = gb.getFeatures() || {};

  Object.keys(allFeatures).forEach((key) => {
    features[key] = gb.getFeatureValue(key, false);
  });

  return features;
};

// Utility to check if GrowthBook is ready
export const useIsGBReady = (): boolean => {
  const gb = useVeroGrowthBook();
  return gb !== null && gb.ready;
};

/**
 * Hook to set the VRN location context for feature flag evaluation
 * Use this when you need to evaluate flags based on a specific property, portfolio, or company
 * The location is used for targeting rules in GrowthBook (e.g., enabling features per-property)
 *
 * @param {string} location - The VRN location (property, portfolio, or company format)
 */
export const useSetVeroFlagLocation = (location?: string): void => {
  const gb = useGBHook();

  useEffect(() => {
    if (!gb || !gb.setAttributes || !location) return;

    const currentAttributes = gb.getAttributes ? gb.getAttributes() : {};

    if (currentAttributes.propertyLocation === location) return;

    const newAttributes = {
      ...currentAttributes,
      propertyLocation: location,
    };

    gb.setAttributes(newAttributes);
  }, [gb, location]);
};
